import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Tag, Button, Modal, Form, Select, Input, Spin, Empty,
  Drawer, Descriptions, Badge, Progress, Tooltip,
} from 'antd';
import {
  UserPlus, Edit2, Trash2, Eye, Unlink, Search, Users, Link2,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { PEOPLE, PEOPLE_AVAILABLE } from '../../constants/api';
import ToastMsg from '../../components/common/ToastMsg';
import ExportButton from '../../components/common/ExportButton';
import { flattenForExport } from '../../lib/exportUtils';
import { MetricCard } from '../../components/insights';
import { AuthContext } from '../../context/AuthContext';

const PeoplePage = () => {
  const { fetch, create, update, remove } = useApiMethods();
  const { hasPermission } = useContext(AuthContext);
  const canSeeFinance = hasPermission('finance_sensitive', 'view');
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const [hrmsOptions, setHrmsOptions] = useState([]);
  const [crmOptions, setCrmOptions] = useState([]);
  const [fixdeskOptions, setFixdeskOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [profileOpen, setProfileOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const toArray = (res) => {
    const d = res?.data || res;
    return Array.isArray(d) ? d : [];
  };

  const loadMappings = useCallback(async () => {
    const res = await fetch(PEOPLE, {}, setLoading);
    if (res?.status !== 'fail') {
      const list = res?.data || res;
      setMappings(Array.isArray(list) ? list : []);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadMappings(); }, [loadMappings]);

  const loadOptions = async () => {
    setLoadingOptions(true);
    const [hrms, crm, fixdesk] = await Promise.all([
      fetch(`${PEOPLE_AVAILABLE}`, { source: 'hrms' }),
      fetch(`${PEOPLE_AVAILABLE}`, { source: 'crm' }),
      fetch(`${PEOPLE_AVAILABLE}`, { source: 'fixdesk' }),
    ]);
    setHrmsOptions(toArray(hrms));
    setCrmOptions(toArray(crm));
    setFixdeskOptions(toArray(fixdesk));
    setLoadingOptions(false);
  };

  const openAdd = () => { setEditingId(null); form.resetFields(); loadOptions(); setModalOpen(true); };
  const openEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({ name: record.name, email: record.email, hrms_employee_id: record.hrms_employee_id, crm_user_id: record.crm_user_id, fixdesk_member_id: record.fixdesk_member_id });
    loadOptions();
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const hrmsMatch = hrmsOptions.find(o => o.id === values.hrms_employee_id);
      const crmMatch = crmOptions.find(o => o.id === values.crm_user_id);
      const fixdeskMatch = fixdeskOptions.find(o => o.id === values.fixdesk_member_id);

      const payload = {
        name: values.name, email: values.email || '',
        hrms_employee_id: values.hrms_employee_id || null, hrms_employee_name: hrmsMatch?.name || '',
        crm_user_id: values.crm_user_id || null, crm_user_name: crmMatch?.name || '',
        fixdesk_member_id: values.fixdesk_member_id || null, fixdesk_member_name: fixdeskMatch?.name || '',
      };

      const res = editingId
        ? await update(`${PEOPLE}${editingId}/`, payload, false, setSaving)
        : await create(PEOPLE, payload, false, setSaving);

      if (res?.status === 'success') {
        ToastMsg('success', editingId ? 'Updated' : 'Created');
        setModalOpen(false);
        loadMappings();
      } else {
        ToastMsg('error', res?.message || 'Failed');
      }
    } catch (err) { /* validation */ }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: `Remove ${record.name}?`,
      okText: 'Delete', okType: 'danger',
      onOk: async () => {
        const res = await remove(`${PEOPLE}${record.id}/`);
        if (res?.status === 'success') { ToastMsg('success', 'Deleted'); loadMappings(); }
      },
    });
  };

  const openProfile = async (record) => {
    setProfileOpen(true);
    setProfileLoading(true);
    const res = await fetch(`${PEOPLE}${record.id}/profile/`);
    if (res?.status !== 'fail') setProfile(res?.data || res);
    setProfileLoading(false);
  };

  const onDropdownSelect = (source, value) => {
    const options = source === 'hrms' ? hrmsOptions : source === 'crm' ? crmOptions : fixdeskOptions;
    const match = options.find(o => o.id === value);
    if (match && !form.getFieldValue('name')) {
      form.setFieldsValue({ name: match.name, email: match.email || '' });
    }
  };

  const filtered = search
    ? mappings.filter(m => m.name?.toLowerCase().includes(search.toLowerCase()) || m.email?.toLowerCase().includes(search.toLowerCase()))
    : mappings;

  const linkedCount = (m) => [m.hrms_employee_id, m.crm_user_id, m.fixdesk_member_id].filter(Boolean).length;

  if (loading) return <div className="flex items-center justify-center h-screen"><Spin size="large" /></div>;

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">People</h2>
          <p className="text-sm text-muted-foreground">Map employees across HRMS, CRM, and FixDesk</p>
        </div>
        <div className="flex gap-3">
          <ExportButton
            data={flattenForExport(mappings, { Name: 'name', Email: 'email', 'HRMS Employee': 'hrms_employee_name', 'CRM User': 'crm_user_name', 'FixDesk Member': 'fixdesk_member_name' })}
            filename="people-mapping"
            pdfColumns={[{title:'Name',dataKey:'Name'},{title:'Email',dataKey:'Email'},{title:'HRMS',dataKey:'HRMS Employee'},{title:'CRM',dataKey:'CRM User'},{title:'FixDesk',dataKey:'FixDesk Member'}]}
            pdfTitle="Employee Mapping Report"
          />
          <Button type="primary" icon={<UserPlus size={16} />} onClick={openAdd} style={{ backgroundColor: '#4f46e5', borderRadius: 10, height: 40 }}>
            Add Mapping
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={Users} intent="neutral" title="Total Mapped" value={mappings.length} />
        <MetricCard icon={Link2} intent="primary" title="HRMS Linked" value={mappings.filter(m => m.hrms_employee_id).length} subtitle={`${mappings.length ? Math.round(mappings.filter(m => m.hrms_employee_id).length / mappings.length * 100) : 0}% of mapped`} />
        <MetricCard icon={Link2} intent="info" title="CRM Linked" value={mappings.filter(m => m.crm_user_id).length} subtitle={`${mappings.length ? Math.round(mappings.filter(m => m.crm_user_id).length / mappings.length * 100) : 0}% of mapped`} />
        <MetricCard icon={Link2} intent="positive" title="FixDesk Linked" value={mappings.filter(m => m.fixdesk_member_id).length} subtitle={`${mappings.length ? Math.round(mappings.filter(m => m.fixdesk_member_id).length / mappings.length * 100) : 0}% of mapped`} />
      </div>

      {/* Search + List */}
      <div className="rounded-2xl bg-card border border-border shadow-sm">
        <div className="p-4 border-b border-border">
          <Input
            prefix={<Search size={16} className="text-muted-foreground" />}
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-xl"
            allowClear
          />
        </div>
        <div className="divide-y divide-gray-50">
          {filtered.length === 0 ? (
            <div className="p-12"><Empty description={search ? 'No matches' : 'No employee mappings yet'} /></div>
          ) : (
            filtered.map((m) => (
              <div key={m.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => openProfile(m)}>
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-sm shrink-0">
                  {m.name?.charAt(0)?.toUpperCase()}
                </div>

                {/* Name + email */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-foreground">{m.name}</div>
                  {m.email && <div className="text-xs text-muted-foreground truncate">{m.email}</div>}
                </div>

                {/* System badges */}
                <div className="flex gap-2 shrink-0">
                  {m.hrms_employee_id ? (
                    <Tooltip title={`HRMS: ${m.hrms_employee_name}`}>
                      <span className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-purple-50 dark:bg-purple-950/30 text-purple-600 border border-purple-100">HRMS</span>
                    </Tooltip>
                  ) : (
                    <span className="px-2 py-1 rounded-lg text-[10px] text-gray-300 border border-border">HRMS</span>
                  )}
                  {m.crm_user_id ? (
                    <Tooltip title={`CRM: ${m.crm_user_name}`}>
                      <span className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-blue-50 dark:bg-blue-950/30 text-blue-600 border border-blue-100">CRM</span>
                    </Tooltip>
                  ) : (
                    <span className="px-2 py-1 rounded-lg text-[10px] text-gray-300 border border-border">CRM</span>
                  )}
                  {m.fixdesk_member_id ? (
                    <Tooltip title={`FixDesk: ${m.fixdesk_member_name}`}>
                      <span className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-green-50 dark:bg-green-950/30 text-green-600 border border-green-100">FixDesk</span>
                    </Tooltip>
                  ) : (
                    <span className="px-2 py-1 rounded-lg text-[10px] text-gray-300 border border-border">FixDesk</span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors" onClick={() => openEdit(m)}>
                    <Edit2 size={14} className="text-muted-foreground" />
                  </button>
                  <button className="p-2 rounded-lg hover:bg-red-50 transition-colors" onClick={() => handleDelete(m)}>
                    <Trash2 size={14} className="text-muted-foreground hover:text-red-500" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        title={editingId ? 'Edit Mapping' : 'Add Employee Mapping'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        okText={editingId ? 'Update' : 'Create'}
        okButtonProps={{ style: { backgroundColor: '#4f46e5', borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        width={520}
        styles={{ body: { paddingTop: 16 } }}
      >
        <Spin spinning={loadingOptions}>
          <Form form={form} layout="vertical">
            <Form.Item name="name" label="Employee Name" rules={[{ required: true }]}>
              <Input placeholder="Name" className="rounded-lg" />
            </Form.Item>
            <Form.Item name="email" label="Email">
              <Input placeholder="Email" className="rounded-lg" />
            </Form.Item>
            <Form.Item name="hrms_employee_id" label={<span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-purple-500" /> HRMS Employee</span>}>
              <Select placeholder="Select HRMS employee" allowClear showSearch optionFilterProp="label"
                onChange={(v) => onDropdownSelect('hrms', v)}
                options={hrmsOptions.map(o => ({ value: o.id, label: `${o.name} (${o.email || 'no email'})` }))}
              />
            </Form.Item>
            <Form.Item name="crm_user_id" label={<span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-500" /> CRM User</span>}>
              <Select placeholder="Select CRM user" allowClear showSearch optionFilterProp="label"
                onChange={(v) => onDropdownSelect('crm', v)}
                options={crmOptions.map(o => ({ value: o.id, label: `${o.name} (${o.email || 'no email'})` }))}
              />
            </Form.Item>
            <Form.Item name="fixdesk_member_id" label={<span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500" /> FixDesk Member</span>}>
              <Select placeholder="Select FixDesk member" allowClear showSearch optionFilterProp="label"
                onChange={(v) => onDropdownSelect('fixdesk', v)}
                options={fixdeskOptions.map(o => ({ value: o.id, label: `${o.name} (${o.email || 'no email'})` }))}
              />
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Profile Drawer */}
      <Drawer title={null} open={profileOpen} onClose={() => { setProfileOpen(false); setProfile(null); }} width={520}
        styles={{ body: { padding: 0 } }}
      >
        {profileLoading ? (
          <div className="flex items-center justify-center h-64"><Spin size="large" /></div>
        ) : profile ? (
          <div>
            {/* Profile header */}
            <div className="bg-gradient-to-r from-indigo-500 to-violet-500 p-6 text-white">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold mb-3">
                {profile.name?.charAt(0)?.toUpperCase()}
              </div>
              <h3 className="text-xl font-bold">{profile.name}</h3>
              {profile.email && <p className="text-white/70 text-sm mt-1">{profile.email}</p>}
              <div className="flex gap-2 mt-3">
                {profile.hrms && <span className="px-2 py-1 rounded-lg text-xs bg-white/20">HRMS</span>}
                {profile.crm && <span className="px-2 py-1 rounded-lg text-xs bg-white/20">CRM</span>}
                {profile.fixdesk && <span className="px-2 py-1 rounded-lg text-xs bg-white/20">FixDesk</span>}
              </div>
            </div>

            <div className="p-5 space-y-5">
              {/* HR Section */}
              {profile.hrms && (
                <div className="rounded-xl border border-purple-100 p-4">
                  <h4 className="text-sm font-semibold text-purple-700 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-purple-500" /> HR Data
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-muted-foreground text-xs">Department</span><div className="font-medium">{profile.hrms.department || '-'}</div></div>
                    <div><span className="text-muted-foreground text-xs">Designation</span><div className="font-medium">{profile.hrms.designation || '-'}</div></div>
                    <div><span className="text-muted-foreground text-xs">Attendance</span><div className="font-medium">{profile.hrms.attendance?.attendance_pct ?? '-'}%</div></div>
                    <div><span className="text-muted-foreground text-xs">Present Days</span><div className="font-medium">{profile.hrms.attendance?.present_days ?? '-'}</div></div>
                    {canSeeFinance && <div><span className="text-muted-foreground text-xs">Monthly CTC</span><div className="font-medium">{profile.hrms.monthly_ctc ? `₹${Number(profile.hrms.monthly_ctc).toLocaleString()}` : '-'}</div></div>}
                    {canSeeFinance && <div><span className="text-muted-foreground text-xs">Net Salary</span><div className="font-medium">{profile.hrms.net_salary ? `₹${Number(profile.hrms.net_salary).toLocaleString()}` : '-'}</div></div>}
                  </div>
                </div>
              )}

              {/* CRM Section */}
              {profile.crm && (
                <div className="rounded-xl border border-blue-100 p-4">
                  <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500" /> CRM Data
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-muted-foreground text-xs">Role</span><div className="font-medium">{profile.crm.role_type || '-'}</div></div>
                    <div><span className="text-muted-foreground text-xs">Emp Code</span><div className="font-medium">{profile.crm.emp_code || '-'}</div></div>
                    <div><span className="text-muted-foreground text-xs">Email</span><div className="font-medium">{profile.crm.email || '-'}</div></div>
                    <div><span className="text-muted-foreground text-xs">Phone</span><div className="font-medium">{profile.crm.phone || '-'}</div></div>
                  </div>
                </div>
              )}

              {/* FixDesk Section */}
              {profile.fixdesk && (
                <div className="rounded-xl border border-green-100 p-4">
                  <h4 className="text-sm font-semibold text-green-700 dark:text-green-300 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500" /> Development Data
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-muted-foreground text-xs">Designation</span><div className="font-medium">{profile.fixdesk.designation || '-'}</div></div>
                    <div><span className="text-muted-foreground text-xs">Team</span><div className="font-medium">{profile.fixdesk.team_name || '-'}</div></div>
                    <div><span className="text-muted-foreground text-xs">Member Code</span><div className="font-medium">{profile.fixdesk.member_code || '-'}</div></div>
                    <div><span className="text-muted-foreground text-xs">Email</span><div className="font-medium">{profile.fixdesk.email || '-'}</div></div>
                  </div>
                  {profile.fixdesk_workload && (
                    <div className="flex gap-2 mt-3">
                      <span className="px-2 py-1 rounded-lg text-xs bg-gray-100">{profile.fixdesk_workload.total} tickets</span>
                      <span className="px-2 py-1 rounded-lg text-xs bg-blue-50 dark:bg-blue-950/30 text-blue-600">{profile.fixdesk_workload.in_progress} active</span>
                      <span className="px-2 py-1 rounded-lg text-xs bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400">{profile.fixdesk_workload.pending} pending</span>
                      <span className="px-2 py-1 rounded-lg text-xs bg-gray-100">{profile.fixdesk_workload.dev_hours}h</span>
                    </div>
                  )}
                </div>
              )}

              {!profile.hrms && !profile.crm && !profile.fixdesk && (
                <Empty description="No system data linked" />
              )}
            </div>
          </div>
        ) : (
          <Empty description="Profile not found" className="mt-20" />
        )}
      </Drawer>
    </div>
  );
};

export default PeoplePage;
